import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import Truncate from 'react-truncate';

// CONSTANTS
import { contentStatuses, DisplayType, ContentType } from '@/constants/watchList';
import { actions, triggered } from '@/constants/analytics';
import { LOCATIONS_CONTENT_HOVER, LOCATIONS_CONTENT_MODAL } from '@/constants/locations';

// UTILITY
import { getRuntime, isEvent } from '@/utility/common';
import { getYearRange, getContentYear, getDuration } from '@/utility/content';
import { externalLink, getExternalUrlParams } from '@/utility/analytics';

// STORE
import { AuthSelectors } from '@/store/selectors';

// COMPONENTS
import AddRemoveButton from '@/components/Buttons/AddRemoveButton';

// HOC
import Guard from '@/hoc/Guard';

// STYLES
import { ShowMovieCardContentStyled } from '../styles';

// HELP
import ShowMovieCard from './ShowMovieCard';
import DetailsButton from './DetailsButton';
import WatchButton from './WatchButton';
import AppsList from './AppsList';
import Scores from '@/components/ShowMovies/Scores/Scores';

function getGenres(genres = [], count = 1, separator = '/') {
  return genres
    .slice(0, count)
    .map(({ name }) => name)
    .join(separator);
}

function getStringWithoutSpaces(subtitles) {
  return subtitles
    .filter(Boolean)
    .map(str => str.replace(/\s/g, '\u00A0'))
    .join(',\n');
}

function ShowMovieCardWithContent({
  isAuth,
  partner,
  className,
  data,
  actionLoading,
  cardProps,
  displayType,
  analyticsLocation,
  page,
  ...props
}) {
  const [t] = useTranslation();
  const uniqueId = useSelector(AuthSelectors.getUniqueId);
  const anonymousId = useSelector(AuthSelectors.getAnonymousId);

  const {
    id,
    title,
    duration,
    genres,
    type,
    year_range,
    status,
    first_air_date,
    overview,
    in_watchlist,
    user_content_status,
    seasons_count,
    rating,
  } = data;
  const unique_id = uniqueId || anonymousId;
  const externalUrlParams = getExternalUrlParams({ unique_id });
  const isDialog = displayType === DisplayType.DIALOG;
  const isTooltip = displayType === DisplayType.TOOLTIP;
  const isShow = type === ContentType.SHOW;
  const hasApps = data.apps?.length > 0;
  const isAppButton = !!data.app_button;

  function handleExternalLink(e, analyticsData) {
    const property = {
      ...analyticsData,
      location: LOCATIONS_CONTENT_HOVER,
      pageTitle: document.title,
      href: e.target.href || '',
      unique_id,
      content_id: id,
      content_title: title,
    };

    externalLink(property);
  }

  function handleWatchNowСlick() {
    const { currentlyWatching } = contentStatuses;

    const needChangeContentStatus = user_content_status !== currentlyWatching;

    if (needChangeContentStatus) {
      props.changeContentStatus(id, currentlyWatching);
    }
  }

  const genresTitle = getGenres(genres, 1, '/');
  const yearTitle = isShow
    ? getYearRange(year_range, status, t('common.movie.current'))
    : getContentYear(first_air_date);
  const durationTitle = isShow ? getRuntime(duration) : getDuration(duration);
  const seasonsCount = isShow
    ? t('common.seasonsCount', { number: seasons_count, count: seasons_count })
    : null;

  const contentSubtitleData = React.useMemo(
    () =>
      isDialog
        ? [genresTitle, yearTitle, seasonsCount, status, rating]
        : [genresTitle, yearTitle, durationTitle],
    [isDialog],
  );

  const contentSubtitle = React.useMemo(() => {
    return getStringWithoutSpaces(contentSubtitleData);
  }, [contentSubtitleData]);

  const analyticsData = {
    triggered: isShow ? triggered.show : triggered.movie,
    action: actions.addToWatchlist,
    content_id: id,
    partner_id: partner.id || 0,
    location: analyticsLocation,
  };

  function addToWatchList(e) {
    if (isEvent(e)) {
      e.stopPropagation();
    }

    props.addToWatchList(data);
  }

  function removeFromWatchList(e) {
    if (isEvent(e)) {
      e.stopPropagation();
    }

    props.removeFromWatchList(data);
  }

  return (
    <ShowMovieCardContentStyled
      className={clsx(className, {
        [`movie-content--${displayType}`]: displayType,
        [`movie-content--${page}`]: page,
      })}
    >
      {isDialog && (
        <ShowMovieCard
          {...cardProps}
          data={data}
          className="movie-content__section"
          displayType={displayType}
        />
      )}
      <div className="movie-content__section">
        {isTooltip && (
          <Typography component="h3" align="center" className="p5 movie-content__title">
            {title}
          </Typography>
        )}
        <Typography
          component="h4"
          align="center"
          className={clsx('movie-content__subtitle', isDialog && 'p4')}
          color="textSecondary"
        >
          {contentSubtitle}
        </Typography>
        <Scores
          imdb={data?.imdb_rating || ''}
          rTomatoes={data?.rotten_tomatoes?.toString() || ''}
          type="card"
        />
        <Divider className="movie-content__divider" />
        <Typography className={clsx('movie-content__description', isDialog && 'p4')}>
          <Truncate lines={7} ellipsis="..." trimWhitespace>
            {overview}
          </Truncate>
        </Typography>
        {isTooltip && <div className="movie-content__indent" />}
        {hasApps && (
          <>
            <AppsList
              className="movie-content__apps"
              isAuth={isAuth}
              data={data.apps}
              displayType={displayType}
              externalUrlParams={externalUrlParams}
              handleExternalLink={handleExternalLink}
              handleWatchNowСlick={handleWatchNowСlick}
              analyticsLocation={analyticsLocation}
            />
          </>
        )}
        {isTooltip && <div className="movie-content__indent" />}
        {isAppButton ? (
          data.app_button.link && (
            <WatchButton
              analyticsLocation={analyticsLocation}
              partner={partner}
              data={data.app_button}
              onWatchNowClick={handleWatchNowСlick}
              className="movie-content__watchlist-btn sizeExtraLarge"
            />
          )
        ) : (
          <Guard iframe={false} partner={{ use_authorizing: true }}>
            <AddRemoveButton
              className="movie-content__watchlist-btn sizeExtraLarge"
              variant="outlined"
              color="primary"
              disabled={actionLoading}
              loading={actionLoading}
              onClick={addToWatchList}
              handleDelete={removeFromWatchList}
              added={in_watchlist}
              fullWidth
              removeText={t('buttons.remove')}
              checkAuth
              minWidth={160}
              checkAuthProps={{
                authComponentProps: {
                  analyticsData,
                },
              }}
            >
              {t(`buttons.${in_watchlist ? 'inWatchlist' : 'addToWatchlist'}`)}
            </AddRemoveButton>
          </Guard>
        )}
        {isDialog && (
          <DetailsButton
            location={LOCATIONS_CONTENT_MODAL}
            data={data}
            className="movie-content__details-btn sizeExtraLarge"
            variant="contained"
            color="primary"
            fullWidth
          >
            {t('buttons.seeDetails')}
          </DetailsButton>
        )}
      </div>
    </ShowMovieCardContentStyled>
  );
}

ShowMovieCardWithContent.defaultProps = {
  className: '',
  actionLoading: false,
  isAuth: false,
  displayType: DisplayType.TOOLTIP,
  cardProps: {},
  partner: {},
};

ShowMovieCardWithContent.propTypes = {
  className: PropTypes.string,
  displayType: PropTypes.string,
  page: PropTypes.string,
  analyticsLocation: PropTypes.string,
  isAuth: PropTypes.bool,
  actionLoading: PropTypes.bool,
  cardProps: PropTypes.object,
  data: PropTypes.object.isRequired,
  addToWatchList: PropTypes.func.isRequired,
  removeFromWatchList: PropTypes.func.isRequired,
  changeContentStatus: PropTypes.func.isRequired,
  partner: PropTypes.object,
};

export default React.memo(ShowMovieCardWithContent);
